import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import Card from '@packages/ui/card/card.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import { useTranslate } from '@tolgee/react';
import { useState } from 'react';
import { useCreateTable } from '../logic/use-create-table';

interface TableInput {
  name: string;
  seats: number;
}

export default function CreateTableModal(props: { tableGroupId: string; onClose: () => void }) {
  const { t } = useTranslate();
  const { showAlert } = useNotificationCenter();

  const [create, { error, loading }] = useCreateTable();

  const [table, setTable] = useState<TableInput>({ name: '', seats: 1 });
  const [emptyValidation, setEmptyValidation] = useState<Record<string, boolean>>({
    name: false,
  });

  const isPassValidation = () => {
    let flag = true;
    if (table.name == '') {
      setEmptyValidation((item) => ({
        ...item,
        name: true,
      }));
      flag = false;
    }

    return flag;
  };

  const onSubmit = () => {
    if (!isPassValidation()) return;

    create({
      variables: {
        tableGroup: props.tableGroupId,
        data: {
          name: table.name.trim(),
          seats: table.seats,
        },
      },
    })
      .then(() => {
        props.onClose();
        showAlert({
          status: 'success',
          message: t('table.create.alert.success.message', 'Successfully created table'),
          title: t('table.create.alert.success.title', 'Success'),
        });
      })
      .catch((e: Error) => {
        showAlert({
          status: 'error',
          message: e.message,
          title: t('table.create.alert.error.title', 'Error'),
        });
      });
  };

  return (
    <Card>
      <CardHeader title={t('table.create.header', 'Create Table')} onCrossButton={props.onClose} />

      <ApplicationErrorView error={error} />

      <CardContent>
        <FormLayout>
          <FormItem title={t('table.name.title', 'Name')}>
            <TextInput
              type="text"
              label="name"
              value={table.name}
              placeholder={t('table.name.placeholder', 'Name')}
              name="name"
              error={emptyValidation.name ? t('table.create.empty-error.name', 'Name is require') : undefined}
              onChange={(e) => {
                setTable({
                  ...table,
                  name: e.target.value,
                });
              }}
            />
          </FormItem>

          <FormItem title={t('table.seats.title', 'Seats')}>
            <TextInput
              type="number"
              label="seats"
              value={table.seats}
              placeholder={t('table.seats.placeholder', 'Seats')}
              name="seats"
              onChange={(e) => {
                setTable({
                  ...table,
                  seats: Number(e.target.value),
                });
              }}
            />
          </FormItem>
        </FormLayout>
      </CardContent>

      <CardFooter>
        <PrimaryButton loading={loading} onClick={onSubmit}>
          {t('common.button-create', 'Create')}
        </PrimaryButton>
        <SecondaryButton onClick={props.onClose}>{t('common.button.cancel', 'Cancel')}</SecondaryButton>
      </CardFooter>
    </Card>
  );
}
